import { captureException } from '@/lib/errorReporting';
import { UiAlert, UiButton } from '@/ui';
import { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = { hasError: false };

  public static getDerivedStateFromError() {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Global error boundary caught:', error, errorInfo);
    captureException(error, {
      componentStack: errorInfo.componentStack ?? '',
    });
  }

  private handleReload = () => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className='p-8'>
          <div className='max-w-2xl mx-auto'>
            <UiAlert severity='error'>
              <div className='font-semibold mb-2'>Something went wrong</div>
              <div className='mb-4'>
                An unexpected error occurred. Please reload the page or contact support if the issue
                persists.
              </div>
              <UiButton variant='primary' onClick={this.handleReload}>
                Reload application
              </UiButton>
            </UiAlert>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
